import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
    AfterContentInit,
    Component,
    ContentChildren,
    ElementRef,
    forwardRef,
    HostBinding,
    HostListener,
    Input,
    NgZone,
    OnDestroy,
    QueryList,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { fromEvent, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { SelectItemComponent } from './select-item/select-item.component';
import { SelectSearchParent, SELECT_SEARCH_PARENT } from './select-search.token';

const PANEL_POSITIONS: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
];

@Component({
    selector: 'app-select-search',
    templateUrl: './select-search.component.html',
    styleUrls: ['./select-search.component.scss'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SelectSearchComponent), multi: true },
        { provide: SELECT_SEARCH_PARENT, useExisting: forwardRef(() => SelectSearchComponent) },
    ],
})
export class SelectSearchComponent implements AfterContentInit, ControlValueAccessor, OnDestroy, SelectSearchParent {
    @HostBinding('attr.aria-label')
    @Input()
    placeholder = '';

    // Coerced so that a bare `multiple` attribute works, as it does on mat-select
    @Input()
    get multiple() {
        return this._multiple;
    }
    set multiple(multiple: any) {
        this._multiple = coerceBooleanProperty(multiple);
    }

    @Input()
    compareWith = (a: any, b: any) => a === b;

    @ContentChildren(SelectItemComponent)
    items: QueryList<SelectItemComponent>;

    @ViewChild('panel', { static: true })
    panelTemplate: TemplateRef<any>;

    @ViewChild('searchInput')
    set searchInputRef(input: ElementRef<HTMLInputElement>) {
        if (input) {
            input.nativeElement.focus();
        }
    }

    @HostBinding('attr.role')
    role = 'combobox';

    @HostBinding('class.select-search--open')
    @HostBinding('attr.aria-expanded')
    open = false;

    @HostBinding('class.select-search--disabled')
    @HostBinding('attr.aria-disabled')
    disabled = false;

    @HostBinding('attr.tabindex')
    get tabIndex() {
        return this.disabled || this.open ? -1 : 0;
    }

    value: any;

    // A field rather than a getter: the panel's view is checked before the items query updates,
    // so a getter would change value in the middle of a check when items arrive while it's open
    noMatches = false;

    private _searchText = '';
    get searchText() {
        return this._searchText;
    }
    set searchText(text: string) {
        if (text === this._searchText) {
            return;
        }
        this._searchText = text;
        this._filterItems();
        if (this.open) {
            this._activateDefaultItem();
            this._overlayRef.overlayElement.firstElementChild.scrollTop = 0;
            // Clearing the search can grow the panel past the viewport from where it was placed
            this._refreshPanelWhenStable();
        }
    }

    get selectedItems() {
        return this.items.filter(item => item.selected);
    }

    get selectedText() {
        return this.selectedItems.map(item => item.text).join(', ');
    }

    get labelFloats() {
        return this.open || this.selectedItems.length > 0;
    }

    get activeItemId() {
        return this._keyManager.activeItem?.id;
    }

    private _keyManager: ActiveDescendantKeyManager<SelectItemComponent>;
    private _multiple = false;
    private _overlayRef: OverlayRef;
    private _pressedInSearch = false;
    private _subscriptions: Subscription[] = [];
    private _panelSubscriptions: Subscription[] = [];
    private _onChange = (_: any) => {};
    private _onTouched = () => {};

    constructor(
        private _elementRef: ElementRef<HTMLElement>,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private _ngZone: NgZone
    ) {}

    ngAfterContentInit() {
        this._keyManager = new ActiveDescendantKeyManager(this.items)
            .withWrap()
            .skipPredicate(item => item.hidden);
        this._subscriptions.push(
            // Items can arrive while the panel is open. Deferred because this fires during change detection.
            this.items.changes.subscribe(() => {
                if (this.open) {
                    Promise.resolve().then(() => this._onItemsChanged());
                }
            })
        );
    }

    ngOnDestroy() {
        this._closeSubscriptions();
        this._subscriptions.forEach(s => s.unsubscribe());
        this._subscriptions = [];
        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }

    writeValue(value: any) {
        this.value = value;
    }

    registerOnChange(fn: (value: any) => void) {
        this._onChange = fn;
    }

    registerOnTouched(fn: () => void) {
        this._onTouched = fn;
    }

    setDisabledState(disabled: boolean) {
        this.disabled = disabled;
        if (disabled) {
            this.closePanel();
        }
    }

    @HostListener('click')
    toggle() {
        if (!this.open) {
            this.openPanel();
        } else if (!this._pressedInSearch) {
            this.closePanel();
        }
    }

    @HostListener('blur')
    onBlur() {
        if (!this.open) {
            this._onTouched();
        }
    }

    @HostListener('keydown', ['$event'])
    onKeydown(event: KeyboardEvent) {
        if (!this.open && ['Enter', ' ', 'ArrowDown'].includes(event.key)) {
            event.preventDefault();
            // The panel starts listening for keys as it opens. If this press carried on up to it,
            // the Enter that opened the list would also pick an item.
            event.stopPropagation();
            this.openPanel();
        }
    }

    openPanel() {
        if (this.disabled || this.open) {
            return;
        }
        if (!this._overlayRef) {
            this._overlayRef = this._createOverlay();
        }
        this.open = true;
        this._filterItems();
        this._activateDefaultItem();
        this._updatePanelWidth();
        this._overlayRef.attach(new TemplatePortal(this.panelTemplate, this._viewContainerRef));
        this._watchPanel();
        this._panelSubscriptions.push(
            this._ngZone.onStable.pipe(take(1)).subscribe(() => {
                // Swapping the value for the input can change the box's width in a shrink-to-fit parent
                this._updatePanelWidth();
                this._scrollToActiveItem();
            })
        );
    }

    closePanel() {
        if (!this.open) {
            return;
        }
        this.open = false;
        this._closeSubscriptions();
        this._overlayRef.detach();
        this._keyManager.setActiveItem(-1);
        this.searchText = '';
        this._onTouched();
    }

    isSelected(value: any) {
        if (this.multiple) {
            return (this.value || []).some((selected: any) => this.compareWith(selected, value));
        }
        return this.compareWith(this.value, value);
    }

    selectValue(value: any) {
        if (this.multiple) {
            // A new array, so ngModel sees a change. Values with no matching item are kept.
            this.value = this.isSelected(value)
                ? this.value.filter((selected: any) => !this.compareWith(selected, value))
                : [...(this.value || []), value];
            this._onChange(this.value);
            return;
        }
        if (!this.compareWith(value, this.value)) {
            this.value = value;
            this._onChange(value);
        }
        this.closePanel();
        this._elementRef.nativeElement.focus();
    }

    private _createOverlay() {
        return this._overlay.create({
            positionStrategy: this._overlay.position()
                .flexibleConnectedTo(this._elementRef)
                .withPositions(PANEL_POSITIONS)
                .withFlexibleDimensions(false)
                .withPush(false)
                .withViewportMargin(8),
            scrollStrategy: this._overlay.scrollStrategies.reposition(),
        });
    }

    private _updatePanelWidth() {
        const width = this._elementRef.nativeElement.getBoundingClientRect().width;
        this._overlayRef.updateSize({ width, maxWidth: width });
    }

    private _watchPanel() {
        const overlayRef = this._overlayRef;
        this._panelSubscriptions.push(
            overlayRef.outsidePointerEvents().subscribe(event => this._onOutsideClick(event)),
            overlayRef.keydownEvents().subscribe(event => {
                if (event.key === 'Escape') {
                    this.closePanel();
                    this._elementRef.nativeElement.focus();
                } else if (event.key === 'Tab') {
                    // Tabbing backward from the input would otherwise land on this box
                    if (event.shiftKey) {
                        this._elementRef.nativeElement.focus();
                    }
                    this.closePanel();
                } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                    event.preventDefault();
                    if (event.key === 'ArrowDown') {
                        this._keyManager.setNextItemActive();
                    } else {
                        this._keyManager.setPreviousItemActive();
                    }
                    this._scrollToActiveItem();
                } else if (event.key === 'Enter' && this._keyManager.activeItem) {
                    this.selectValue(this._keyManager.activeItem.value);
                }
            })
        );
        // The reposition strategy only follows the page and cdkScrollables, not a scrolling element
        // like mat-dialog-content, so element scrolls have to be caught on the way down.
        this._ngZone.runOutsideAngular(() => {
            // Keeps the panel as wide as the box through any layout change, not only a window resize
            const resizeObserver = new (window as any).ResizeObserver(() => {
                this._updatePanelWidth();
                overlayRef.updatePosition();
            });
            resizeObserver.observe(this._elementRef.nativeElement);
            this._panelSubscriptions.push(
                new Subscription(() => resizeObserver.disconnect()),
                fromEvent(window, 'scroll', { capture: true, passive: true }).pipe(
                    filter(event => event.target instanceof Element && !overlayRef.overlayElement.contains(event.target))
                ).subscribe(() => overlayRef.updatePosition()),
                fromEvent<MouseEvent>(document, 'mousedown', { capture: true }).subscribe(event => {
                    this._pressedInSearch = (event.target as Element).classList.contains('select-search__input');
                })
            );
        });
    }

    private _onOutsideClick(event: MouseEvent) {
        const target = event.target as Element;
        // Dragging a text selection out of the input fires a click on whatever contains both ends
        if (this._elementRef.nativeElement.contains(target) || this._pressedInSearch) {
            return;
        }
        // A click on a dialog's backdrop dismisses this list, not the dialog as well. This runs in
        // the capture phase, so stopping it here keeps it from reaching the backdrop.
        if (target.classList.contains('cdk-overlay-backdrop')) {
            event.stopPropagation();
        }
        this.closePanel();
    }

    private _onItemsChanged() {
        if (!this.open) {
            return;
        }
        this._filterItems();
        const active = this._keyManager.activeItem;
        if (!active || active.hidden || !this.items.some(item => item === active)) {
            this._activateDefaultItem();
        }
        // The panel may have grown past the viewport from the size it was placed at
        this._refreshPanelWhenStable();
    }

    // The top match while searching, so Enter picks it, and otherwise the selected item
    private _activateDefaultItem() {
        this._keyManager.setActiveItem(-1);
        if (this._searchText.trim()) {
            this._keyManager.setFirstItemActive();
        } else if (this.selectedItems.length > 0) {
            this._keyManager.setActiveItem(this.selectedItems[0]);
        }
    }

    private _scrollToActiveItem() {
        const item = this._keyManager.activeItem;
        if (item) {
            item.scrollIntoView();
        }
    }

    // Waits for the items' hidden states to render
    private _refreshPanelWhenStable() {
        this._panelSubscriptions.push(
            this._ngZone.onStable.pipe(take(1)).subscribe(() => {
                this._overlayRef.updatePosition();
                this._scrollToActiveItem();
            })
        );
    }

    private _closeSubscriptions() {
        this._panelSubscriptions.forEach(s => s.unsubscribe());
        this._panelSubscriptions = [];
    }

    private _filterItems() {
        const search = this._searchText.trim().toLowerCase();
        this.items.forEach(item => {
            item.hidden = !!search && !item.text.toLowerCase().includes(search);
        });
        this.noMatches = !this.items.some(item => !item.hidden);
    }
}
